import { useEffect, useMemo, useState } from "react";
import { Star, ArrowRight, Copy, TrendingUp, Send, Plus, Trash2, Save, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CHANNELS = ["SMS", "Email"];

function TemplateCard({ tpl, onCopy, onEdit, onDelete, onSend }) {
  return (
    <div data-testid={`review-tpl-${tpl.id}`} className="bg-white border border-neutral-200 p-5">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="font-semibold">{tpl.name}</div>
          <div className="text-xs text-neutral-500">Channel: {tpl.channel}{tpl.is_seed ? " · seed" : ""}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onCopy(tpl)} data-testid={`review-tpl-copy-${tpl.id}`} className="lf-btn-outline text-xs inline-flex items-center gap-1.5">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button onClick={() => onEdit(tpl)} data-testid={`review-tpl-edit-${tpl.id}`} className="lf-btn-outline text-xs">Edit</button>
          <button onClick={() => onSend(tpl)} data-testid={`review-tpl-send-${tpl.id}`} className="lf-btn-primary text-xs inline-flex items-center gap-1.5">
            <Send className="w-3.5 h-3.5" /> Send
          </button>
          {!tpl.is_seed && (
            <button onClick={() => onDelete(tpl)} data-testid={`review-tpl-delete-${tpl.id}`} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </div>
      <pre className="whitespace-pre-wrap font-mono text-xs bg-neutral-50 border border-neutral-200 p-3">{tpl.body}</pre>
    </div>
  );
}

function EditorModal({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [channel, setChannel] = useState(initial?.channel || "SMS");
  const [body, setBody] = useState(initial?.body || "");
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;
  const save = async () => {
    if (!name.trim() || !body.trim()) return toast.error("Name and body are required");
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/review-templates/${initial.id}`, { name, channel, body });
        toast.success("Template updated"); onSaved(data);
      } else {
        const { data } = await api.post("/review-templates", { name, channel, body });
        toast.success("Template created"); onSaved(data);
      }
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="review-editor-modal">
      <div className="bg-white max-w-lg w-full p-6 border border-neutral-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold">{editing ? "Edit template" : "New template"}</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-black" data-testid="editor-close"><X className="w-4 h-4"/></button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">Name</div>
            <input value={name} onChange={(e)=>setName(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm" data-testid="editor-name"/>
          </label>
          <label className="block">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">Channel</div>
            <select value={channel} onChange={(e)=>setChannel(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm" data-testid="editor-channel">
              {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">Body</div>
            <textarea value={body} onChange={(e)=>setBody(e.target.value)} rows={6} className="w-full px-3 py-2 border border-neutral-300 text-sm font-mono" data-testid="editor-body"/>
            <div className="text-xs text-neutral-500 mt-1">Merge tags: {"{first_name}"}, {"{business_name}"}, {"{review_link}"}</div>
          </label>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="lf-btn-outline text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="editor-save" className="lf-btn-primary text-sm inline-flex items-center gap-1.5">
            <Save className="w-4 h-4"/>{saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SendModal({ tpl, onClose }) {
  const [businessName, setBusinessName] = useState("");
  const [reviewLink, setReviewLink] = useState("");
  const [csv, setCsv] = useState("Jane,+15551230001,\nJohn,+15551230002,john@example.com\n");
  const [sending, setSending] = useState(false);
  const recipients = useMemo(() => {
    return csv.split("\n").map((l) => l.trim()).filter(Boolean).map((line) => {
      const parts = line.split(",").map((p) => p.trim());
      return { first_name: parts[0] || "", phone: parts[1] || "", email: parts[2] || "" };
    });
  }, [csv]);

  const send = async () => {
    if (!businessName.trim() || !reviewLink.trim()) return toast.error("Business name + review link required");
    if (!recipients.length) return toast.error("Add at least one recipient");
    setSending(true);
    try {
      const { data } = await api.post("/review-campaigns/send", {
        template_id: tpl.id,
        business_name: businessName,
        review_link: reviewLink,
        recipients,
      });
      toast.success(`Batch done: ${data.sent} sent · ${data.skipped} skipped · ${data.failed} failed`);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="review-send-modal">
      <div className="bg-white max-w-lg w-full p-6 border border-neutral-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold">Send: {tpl.name}</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-black" data-testid="send-close"><X className="w-4 h-4"/></button>
        </div>
        <div className="text-xs text-neutral-500 mb-3">Channel: <strong>{tpl.channel}</strong></div>
        <div className="space-y-3">
          <label className="block">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">Business name</div>
            <input value={businessName} onChange={(e)=>setBusinessName(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm" data-testid="send-business" placeholder="e.g. Acme Plumbing"/>
          </label>
          <label className="block">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">Review link</div>
            <input value={reviewLink} onChange={(e)=>setReviewLink(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 text-sm" data-testid="send-link" placeholder="https://g.page/r/..."/>
          </label>
          <label className="block">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1">Recipients CSV (first_name, phone, email — one per line)</div>
            <textarea value={csv} onChange={(e)=>setCsv(e.target.value)} rows={6} className="w-full px-3 py-2 border border-neutral-300 text-xs font-mono" data-testid="send-csv"/>
            <div className="text-xs text-neutral-500 mt-1">{recipients.length} row(s) parsed</div>
          </label>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="lf-btn-outline text-sm">Cancel</button>
          <button onClick={send} disabled={sending} data-testid="send-fire" className="lf-btn-primary text-sm inline-flex items-center gap-1.5">
            <Send className="w-4 h-4"/>{sending ? "Sending…" : `Send to ${recipients.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ReviewTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);     // template or {}
  const [sending, setSending] = useState(null);     // template
  const [editorOpen, setEditorOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/review-templates");
      setTemplates(data.templates || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = async (t) => {
    try { await navigator.clipboard.writeText(t.body); toast.success("Template copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };
  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await api.delete(`/review-templates/${t.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="max-w-4xl mx-auto p-8" data-testid="review-templates-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h1 className="font-display text-3xl font-bold flex items-center gap-3">
          <Star className="w-7 h-7 text-[#E24E1B]" /> Review Generation Templates
        </h1>
        <button onClick={() => { setEditing({}); setEditorOpen(true); }} data-testid="new-template-btn" className="lf-btn-primary text-sm inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4"/> New template
        </button>
      </div>
      <p className="text-sm text-neutral-600 mb-6 max-w-2xl">
        Fire SMS or email review requests to a client's customer list. Pitch as{" "}
        <strong>$197/mo</strong> — clients upload their list, you send the cadence.
      </p>

      <div className="mb-6 bg-[#FFF6EE] border border-[#E5D9CB] p-4 flex items-start gap-2 text-sm">
        <TrendingUp className="w-4 h-4 mt-0.5 text-[#E24E1B] flex-shrink-0" />
        <div>
          <strong>Why this works:</strong> Google reviews are the #1 signal AI Search uses for local rankings.
          Every review earned bumps their <strong>AI Visibility Score</strong> — creating a virtuous upsell
          loop into your Growth / Domination packages.
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading templates…</div>
      ) : (
        <div className="space-y-4">
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              tpl={t}
              onCopy={copy}
              onEdit={(tp) => { setEditing(tp); setEditorOpen(true); }}
              onDelete={del}
              onSend={(tp) => setSending(tp)}
            />
          ))}
          {!templates.length && (
            <div className="text-sm text-neutral-500">No templates yet — click <em>New template</em> above.</div>
          )}
        </div>
      )}

      {editorOpen && (
        <EditorModal
          initial={editing}
          onClose={() => { setEditorOpen(false); setEditing(null); }}
          onSaved={() => load()}
        />
      )}
      {sending && (
        <SendModal tpl={sending} onClose={() => setSending(null)} />
      )}
    </div>
  );
}
